import { useState, useEffect, useCallback } from "react";
import { CashAndBankRepositoryImpl } from "../../data/repositories/CashAndBankRepositoryImpl";
import { GetWeeklyCashFlowUseCase } from "../../domain/use-cases/GetWeeklyCashFlowUseCase";
import type { WeeklyCashFlowEntity } from "../../domain/entities/WeeklyCashFlowEntity";
import type { GetWeeklyCashFlowParams } from "../../domain/repositories/CashAndBankRepository";

const repository = new CashAndBankRepositoryImpl();
const getWeeklyCashFlowUseCase = new GetWeeklyCashFlowUseCase(repository);

export interface UseWeeklyCashFlowResult {
  weeks: WeeklyCashFlowEntity["weeks"];
  insights: WeeklyCashFlowEntity["insights"];
  monthLabel: string;
  mtdNet: string;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useWeeklyCashFlow(
  params?: GetWeeklyCashFlowParams,
): UseWeeklyCashFlowResult {
  const [weeks, setWeeks] = useState<WeeklyCashFlowEntity["weeks"]>([]);
  const [insights, setInsights] = useState<WeeklyCashFlowEntity["insights"]>(
    [],
  );
  const [monthLabel, setMonthLabel] = useState("");
  const [mtdNet, setMtdNet] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const entity = await getWeeklyCashFlowUseCase.execute(params);
      setWeeks(entity.weeks);
      setInsights(entity.insights);
      setMonthLabel(entity.monthLabel);
      setMtdNet(entity.mtdNet);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load weekly cash flow";
      setError(message);
      setWeeks([]);
      setInsights([]);
      setMonthLabel("");
      setMtdNet("");
    } finally {
      setLoading(false);
    }
  }, [params?.year, params?.month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    weeks,
    insights,
    monthLabel,
    mtdNet,
    loading,
    error,
    refetch: fetchData,
  };
}
